import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import VideoCard from '@/components/base/VideoCard';
import SectionHeading from '@/components/base/SectionHeading';
import VideoPlayer from './components/VideoPlayer';
import { useSeo } from '@/hooks/useSeo';
import { videos } from '@/mocks/videos';
import { formatViews, formatLikes, formatDate } from '@/utils/format';

export default function VideoDetailPage() {
  const { videoId = '' } = useParams();
  const video = videos.find((v) => v.id === videoId);
  const [copied, setCopied] = useState(false);

  useSeo({
    title: video ? `${video.title} - 啪啪视频` : '视频未找到 - 啪啪视频',
    description: video
      ? `观看${video.title}高清视频，在线播放并了解简介及相关推荐，尽在啪啪视频。`
      : '该视频不存在或已下线，返回啪啪视频首页发现更多高清视频内容。',
    keywords: video
      ? `啪啪视频,${video.title},${video.categoryName},高清视频,在线播放`
      : '啪啪视频,视频详情',
  });

  if (!video) {
    return (
      <Layout>
        <div className="pt-16">
          <div className="px-4 md:px-6 py-24 text-center">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-file-video-line text-3xl" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">视频未找到</h1>
            <p className="mt-2 text-sm text-foreground-600">你访问的视频可能已下架或链接有误。</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors"
            >
              <i className="ri-home-4-line text-base" />
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const related = videos
    .filter((v) => v.categoryId === video.categoryId && v.id !== video.id)
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 5);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
  };

  const metaItem = (icon: string, label: string, value: string) => (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-accent-500">
        <i className={`${icon} text-base`} />
      </span>
      <span className="text-foreground-500">{label}</span>
      <span className="text-foreground-900 font-medium">{value}</span>
    </div>
  );

  return (
    <Layout>
      <div className="pt-16">
        <div className="px-4 md:px-6 py-6">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-5 flex-wrap" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-base" />
            <Link to={`/category/${video.categoryId}`} className="hover:text-accent-500 transition-colors">
              {video.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line text-base" />
            <span className="text-foreground-900 line-clamp-1">{video.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2">
              <VideoPlayer poster={video.cover} title={video.title} />

              <div className="mt-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <h1 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950 leading-snug">
                    {video.title}
                  </h1>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={handleShare}
                      className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-200/70 text-foreground-800 text-sm font-medium hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
                    >
                      <i className={`${copied ? 'ri-check-line' : 'ri-share-forward-line'} text-base`} />
                      {copied ? '链接已复制' : '分享'}
                    </button>
                    <Link
                      to={`/category/${video.categoryId}`}
                      className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line text-base" />
                      返回分类
                    </Link>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="px-2.5 py-1 rounded-md bg-accent-500/15 text-accent-500 text-xs font-semibold flex items-center gap-1">
                    <i className="ri-star-fill text-[10px]" />
                    {video.rating.toFixed(1)}
                  </span>
                  {video.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/category/${video.categoryId}`}
                      className="px-2.5 py-1 rounded-md bg-secondary-500/15 text-secondary-200 text-xs hover:bg-secondary-500/25 transition-colors"
                    >
                      {t}
                    </Link>
                  ))}
                </div>

                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4 rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-5">
                  {metaItem('ri-price-tag-3-line', '类别', video.categoryName)}
                  {metaItem('ri-calendar-line', '上传日期', formatDate(video.uploadDate))}
                  {metaItem('ri-time-line', '时长', video.duration)}
                  {metaItem('ri-play-circle-line', '播放量', formatViews(video.views))}
                </div>

                <div className="mt-6">
                  <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">简介</h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{video.description}</p>
                </div>
              </div>
            </div>

            <aside className="lg:col-span-1">
              <SectionHeading title="相关推荐" subtitle="同类别精选内容" icon="ri-magic-line" />
              <div className="space-y-3">
                {related.length > 0 ? (
                  related.map((rv) => (
                    <Link
                      key={rv.id}
                      to={`/video/${rv.id}`}
                      className="group flex gap-3 rounded-lg border border-background-200/70 bg-background-100 p-2 hover:border-accent-500 transition-colors"
                    >
                      <div className="relative w-32 sm:w-36 shrink-0 aspect-video rounded-md overflow-hidden bg-background-200">
                        <img
                          src={rv.cover}
                          alt={rv.title}
                          title={`${rv.title} - 啪啪视频`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-background-950/75 text-foreground-900 text-[10px]">
                          {rv.duration}
                        </span>
                      </div>
                      <div className="min-w-0 py-1 pr-1">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-accent-500 transition-colors">
                          {rv.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-500 line-clamp-1">
                          {rv.categoryName} · {formatViews(rv.views)}次播放
                        </p>
                        <p className="mt-0.5 text-xs text-accent-500 flex items-center gap-1">
                          <i className="ri-heart-3-line text-[11px]" />
                          {formatLikes(rv.likes)}
                        </p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-foreground-500 rounded-lg border border-background-200/70 bg-background-100 p-4">
                    暂时没有相关推荐，去分类页看看更多内容吧。
                  </p>
                )}
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <div className="mt-12">
              <SectionHeading title="猜你喜欢" subtitle="根据分类相似度为你推荐" icon="ri-thumb-up-line" />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {related.slice(0, 4).map((rv) => (
                  <VideoCard key={rv.id} video={rv} showCategory={false} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}